import { glideLocalStorage } from "./glideLocalStorage";

const PAYMENT_METHOD_KEY = "glide.lastPaymentMethod";
const DEPOSIT_AMOUNT_KEY = "glide.lastDepositAmount";

export interface SavedPaymentMethodPreference {
  paymentMethod: string;
  timestamp: number;
}

export interface SavedAmountPreference {
  amount: string;
  denomination: "token" | "usd";
  timestamp: number;
}

/**
 * Save the last payment method used by the user
 */
export async function saveLastPaymentMethod(
  paymentMethod: string,
): Promise<void> {
  try {
    const preference: SavedPaymentMethodPreference = {
      paymentMethod,
      timestamp: Date.now(),
    };
    await glideLocalStorage.setItem(
      PAYMENT_METHOD_KEY,
      JSON.stringify(preference),
    );
  } catch (error) {
    console.warn("Failed to save payment method preference:", error);
  }
}

/**
 * Load the last payment method used by the user
 */
export async function loadLastPaymentMethod(): Promise<string | null> {
  try {
    const stored = await glideLocalStorage.getItem(PAYMENT_METHOD_KEY);
    if (!stored) return null;

    const preference = JSON.parse(stored) as SavedPaymentMethodPreference;

    // Expire after 90 days
    const ninetyDaysMs = 90 * 24 * 60 * 60 * 1000;
    if (Date.now() - preference.timestamp > ninetyDaysMs) {
      await clearLastPaymentMethod();
      return null;
    }

    return preference.paymentMethod;
  } catch (error) {
    console.warn("Failed to load payment method preference:", error);
    return null;
  }
}

/**
 * Clear the saved payment method preference
 */
export async function clearLastPaymentMethod(): Promise<void> {
  try {
    await glideLocalStorage.removeItem(PAYMENT_METHOD_KEY);
  } catch (error) {
    console.warn("Failed to clear payment method preference:", error);
  }
}

/**
 * Save the last deposit amount and denomination
 */
export async function saveLastDepositAmount(
  amount: string,
  denomination: "token" | "usd",
): Promise<void> {
  try {
    const preference: SavedAmountPreference = {
      amount,
      denomination,
      timestamp: Date.now(),
    };
    await glideLocalStorage.setItem(
      DEPOSIT_AMOUNT_KEY,
      JSON.stringify(preference),
    );
  } catch (error) {
    console.warn("Failed to save deposit amount preference:", error);
  }
}

/**
 * Load the last deposit amount and denomination
 */
export async function loadLastDepositAmount(): Promise<SavedAmountPreference | null> {
  try {
    const stored = await glideLocalStorage.getItem(DEPOSIT_AMOUNT_KEY);
    if (!stored) return null;

    const preference = JSON.parse(stored) as SavedAmountPreference;

    // Expire after 7 days (amounts are more time-sensitive)
    const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
    if (Date.now() - preference.timestamp > sevenDaysMs) {
      await clearLastDepositAmount();
      return null;
    }

    return preference;
  } catch (error) {
    console.warn("Failed to load deposit amount preference:", error);
    return null;
  }
}

/**
 * Clear the saved deposit amount preference
 */
export async function clearLastDepositAmount(): Promise<void> {
  try {
    await glideLocalStorage.removeItem(DEPOSIT_AMOUNT_KEY);
  } catch (error) {
    console.warn("Failed to clear deposit amount preference:", error);
  }
}

/**
 * Clear all user preferences
 */
export async function clearAllUserPreferences(): Promise<void> {
  await clearLastPaymentMethod();
  await clearLastDepositAmount();
}
